import { useState, useEffect, useRef } from 'react';

interface StreamingTextOptions {
  wordDelay?: number;
  onComplete?: () => void;
}

/**
 * Hook to animate text appearing word by word
 * @param text The full text to animate
 * @param isActive Whether the animation should be active
 * @param options Configuration options
 */
const useStreamingText = (text: string, isActive: boolean, options: StreamingTextOptions = {}) => {
  const [streamedText, setStreamedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { wordDelay = 50, onComplete } = options;

  const cleanup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    setStreamedText('');
    setIsComplete(false);
    cleanup();

    if (!isActive || !text) {
      return cleanup;
    }

    const words = text.split(' ');
    let currentWordIndex = 0;

    const animateNextWord = () => {
      if (currentWordIndex >= words.length) {
        setIsComplete(true);
        onComplete?.();
        return;
      }

      const nextText = words.slice(0, currentWordIndex + 1).join(' ');
      setStreamedText(nextText);
      currentWordIndex += 1;

      timeoutRef.current = setTimeout(animateNextWord, wordDelay);
    };

    timeoutRef.current = setTimeout(animateNextWord, wordDelay);

    return cleanup;
  }, [text, isActive, wordDelay, onComplete]);

  return { streamedText, isComplete };
};

export default useStreamingText;
